<template>
    <div class="activity-selector">
        <label v-if="label" class="selector-label">{{ label }}</label>
        <div class="search-container">
            <div class="search-input-wrapper">
                <i class="pi pi-search search-icon"></i>
                <input
                    v-model="searchQuery"
                    type="text"
                    class="search-input"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    @input="handleSearch"
                    @focus="showDropdown = true"
                />
                <button
                    v-if="selectedActivity"
                    type="button"
                    class="clear-btn"
                    @click="clearSelection"
                >
                    <i class="pi pi-times"></i>
                </button>
            </div>

            <div v-if="showDropdown && (loading || filteredActivities.length > 0)" class="dropdown">
                <div v-if="loading" class="dropdown-loading">
                    <b-spinner small></b-spinner>
                    <span>Searching...</span>
                </div>
                <div
                    v-else
                    v-for="activity in filteredActivities"
                    :key="activity.id"
                    class="dropdown-item"
                    @click="selectActivity(activity)"
                >
                    <div class="activity-info">
                        <span class="activity-ref">{{ activity.reference_no || activity.order_number }}</span>
                        <span v-if="activity.order_type" class="activity-type">{{ activity.order_type.name }}</span>
                    </div>
                    <div class="activity-meta">
                        <span v-if="activity.customer" class="activity-customer">{{ activity.customer.name }}</span>
                        <span class="activity-date">{{ formatDate(activity.created_at) }}</span>
                    </div>
                </div>
                <div v-if="!loading && filteredActivities.length === 0 && searchQuery.length >= 2" class="dropdown-empty">
                    No activities found
                </div>
            </div>
        </div>

        <div v-if="selectedActivity" class="selected-activity">
            <div class="selected-header">
                <i class="pi pi-check-circle"></i>
                <span>Selected Activity</span>
            </div>
            <div class="selected-details">
                <div class="detail-row">
                    <span class="detail-label">Reference:</span>
                    <span class="detail-value">{{ selectedActivity.reference_no || selectedActivity.order_number }}</span>
                </div>
                <div v-if="selectedActivity.order_type" class="detail-row">
                    <span class="detail-label">Type:</span>
                    <span class="detail-value">{{ selectedActivity.order_type.name }}</span>
                </div>
                <div v-if="selectedActivity.customer" class="detail-row">
                    <span class="detail-label">Customer:</span>
                    <span class="detail-value">{{ selectedActivity.customer.name }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Items:</span>
                    <span class="detail-value">{{ selectedActivity.order_items_count || selectedActivity.items?.length || 0 }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash'
import axios from 'axios'

export default {
    name: 'ActivitySelector',
    props: {
        value: {
            type: [Number, Object],
            default: null
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Search by reference number...'
        },
        orderTypeId: {
            type: Number,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            searchQuery: '',
            showDropdown: false,
            loading: false,
            activities: [],
            selectedActivity: null
        }
    },
    computed: {
        filteredActivities() {
            return this.activities
        }
    },
    watch: {
        value: {
            immediate: true,
            async handler(newVal) {
                if (newVal && typeof newVal === 'object') {
                    this.selectedActivity = newVal
                    this.searchQuery = newVal.reference_no || newVal.order_number || ''
                } else if (newVal && typeof newVal === 'number') {
                    await this.fetchActivityById(newVal)
                } else {
                    this.selectedActivity = null
                    this.searchQuery = ''
                }
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        handleSearch: debounce(function() {
            if (this.searchQuery.length >= 2) {
                this.searchActivities()
            } else {
                this.activities = []
            }
        }, 300),

        async searchActivities() {
            this.loading = true
            try {
                const params = { q: this.searchQuery }
                if (this.orderTypeId) {
                    params.order_type_id = this.orderTypeId
                }
                const { data } = await axios.get('order', { params })
                this.activities = data.success?.data?.data || data.data?.data || []
            } catch (err) {
                console.error('Failed to search activities:', err)
                this.activities = []
            } finally {
                this.loading = false
            }
        },

        async fetchActivityById(id) {
            try {
                const { data } = await axios.get(`order/${id}`)
                this.selectedActivity = data.success?.data || data.data
                this.searchQuery = this.selectedActivity?.reference_no || this.selectedActivity?.order_number || ''
            } catch (err) {
                console.error('Failed to fetch activity:', err)
            }
        },

        selectActivity(activity) {
            this.selectedActivity = activity
            this.searchQuery = activity.reference_no || activity.order_number
            this.showDropdown = false
            this.$emit('input', activity.id)
            this.$emit('activity-selected', activity)
        },

        clearSelection() {
            this.selectedActivity = null
            this.searchQuery = ''
            this.activities = []
            this.$emit('input', null)
            this.$emit('activity-selected', null)
        },

        handleClickOutside(e) {
            if (!this.$el.contains(e.target)) {
                this.showDropdown = false
            }
        },

        formatDate(date) {
            if (!date) return ''
            return new Date(date).toLocaleDateString()
        }
    }
}
</script>

<style scoped>
.activity-selector {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.selector-label {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
}

.search-container {
    position: relative;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: #9ca3af;
    font-size: 14px;
}

.search-input {
    width: 100%;
    padding: 12px 40px 12px 40px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
}

.search-input:focus {
    border-color: #2F80ED;
    box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.1);
}

.search-input:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
}

.clear-btn {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 4px;
}

.clear-btn:hover {
    color: #ef4444;
}

.dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    overflow-y: auto;
    z-index: 100;
}

.dropdown-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    color: #6b7280;
}

.dropdown-item {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid #f3f4f6;
    transition: background 0.2s;
}

.dropdown-item:last-child {
    border-bottom: none;
}

.dropdown-item:hover {
    background: #f9fafb;
}

.activity-info {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.activity-ref {
    font-weight: 600;
    color: #111827;
}

.activity-type {
    font-size: 11px;
    padding: 2px 6px;
    background: #e5e7eb;
    border-radius: 4px;
    color: #4b5563;
}

.activity-meta {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #6b7280;
}

.dropdown-empty {
    padding: 16px;
    text-align: center;
    color: #9ca3af;
}

.selected-activity {
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 8px;
    padding: 12px 16px;
}

.selected-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    color: #0369a1;
    font-weight: 600;
    font-size: 13px;
}

.selected-header i {
    color: #22c55e;
}

.selected-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}

.detail-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.detail-label {
    font-size: 11px;
    color: #6b7280;
    text-transform: uppercase;
}

.detail-value {
    font-size: 14px;
    color: #111827;
    font-weight: 500;
}
</style>
